/**
 * Mapeo de colores de vehículo a colores de UI adaptados a cada tema.
 * 
 * Cada tema puede tener sus propias variaciones de los colores de vehículo
 * para mantener la coherencia visual con la paleta del tema.
 */
package link.erpango.quip.theme

import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color

/**
 * Representa los colores de degradado para mostrar en las tarjetas de vehículo.
 * Los colores tienen opacidad baja para mantener un aspecto sutil y profesional.
 */
data class VehicleCardGradient(
    val startColor: Color,
    val endColor: Color = Color.White
) {
    /**
     * Crea un degradado vertical sutil.
     */
    fun toVerticalBrush(): Brush = Brush.verticalGradient(
        colors = listOf(startColor, endColor)
    )
    
    /**
     * Crea un degradado diagonal sutil (esquina superior izquierda a inferior derecha).
     */
    fun toDiagonalBrush(): Brush = Brush.linearGradient(
        colors = listOf(startColor, endColor)
    )
}

/**
 * Objeto que proporciona los colores de degradado para tarjetas de vehículo
 * adaptados al tema actual.
 */
object VehicleColorMapper {
    
    // Opacidad base para el degradado (12% = muy sutil)
    private const val GRADIENT_ALPHA = 0.15f
    private const val GRADIENT_ALPHA_DARK_THEME = 0.25f
    
    /**
     * Obtiene el degradado para una tarjeta de vehículo basándose en el color del vehículo
     * y el tema actual.
     * 
     * @param vehicleColorId ID del color del vehículo (ej: "blue_dark", "red_metallic")
     * @param scheme Esquema de colores actual del tema
     * @param isDarkTheme Si el tema actual es oscuro (para ajustar la opacidad)
     */
    fun getGradientForVehicle(
        vehicleColorId: String,
        scheme: QuipColorScheme = QuipColors.currentScheme,
        isDarkTheme: Boolean = isDarkColorScheme(scheme)
    ): VehicleCardGradient {
        val vehicleColor = VehicleColors.fromId(vehicleColorId)
        val alpha = if (isDarkTheme) GRADIENT_ALPHA_DARK_THEME else GRADIENT_ALPHA
        
        // Obtener el color base, posiblemente ajustado al tema
        val baseColor = adjustColorToTheme(vehicleColor.baseColor, scheme)
        
        // Color de fondo según si es tema oscuro o claro
        val endColor = if (isDarkTheme) {
            scheme.colorBackground
        } else {
            Color.White
        }
        
        return VehicleCardGradient(
            startColor = baseColor.copy(alpha = alpha),
            endColor = endColor
        )
    }
    
    /**
     * Obtiene el degradado usando el nombre del color directamente (para compatibilidad
     * con datos existentes).
     */
    fun getGradientForVehicleByName(
        colorName: String,
        scheme: QuipColorScheme = QuipColors.currentScheme,
        isDarkTheme: Boolean = isDarkColorScheme(scheme)
    ): VehicleCardGradient {
        val vehicleColor = VehicleColors.fromName(colorName)
        return getGradientForVehicle(vehicleColor.id, scheme, isDarkTheme)
    }

    /**
     * Obtiene el degradado usando el id del color directamente.
     */
    fun getGradientForVehicleById(
        colorId: String,
        scheme: QuipColorScheme = QuipColors.currentScheme,
        isDarkTheme: Boolean = isDarkColorScheme(scheme)
    ): VehicleCardGradient {
        return getGradientForVehicle(colorId, scheme, isDarkTheme)
    }

    /**
     * Ajusta un color al tema actual si es necesario.
     * Por ejemplo, en temas muy coloridos, podemos saturar menos los colores neutros.
     */
    private fun adjustColorToTheme(color: Color, scheme: QuipColorScheme): Color {
        // Para colores muy claros (blancos, beiges) en temas claros,
        // usamos un gris muy suave para que se note algo el degradado
        if (color.luminance() > 0.9f && !isDarkColorScheme(scheme)) {
            return Color(0xFFE0E0E0) // Gris muy claro
        }
        
        // Para negros en temas oscuros, usamos un gris oscuro
        if (color.luminance() < 0.1f && isDarkColorScheme(scheme)) {
            return Color(0xFF424242) // Gris oscuro
        }
        
        return color
    }
    
    /**
     * Determina si un esquema de colores es oscuro.
     */
    private fun isDarkColorScheme(scheme: QuipColorScheme): Boolean {
        return scheme.colorBackground.luminance() < 0.5f
    }
    
    /**
     * Calcula la luminancia de un color (aproximación simple).
     */
    private fun Color.luminance(): Float {
        return 0.299f * red + 0.587f * green + 0.114f * blue
    }
    
    /**
     * Obtiene el color de texto óptimo para mostrar sobre el degradado del vehículo.
     * Para degradados muy sutiles, normalmente usaremos el color de texto del tema.
     */
    fun getTextColorForVehicleCard(
        vehicleColorId: String,
        scheme: QuipColorScheme = QuipColors.currentScheme
    ): Color {
        // Como los degradados son muy sutiles (12-25% opacidad),
        // siempre usamos el color de texto del tema
        return scheme.colorText
    }
    
    /**
     * Obtiene el color secundario de texto para detalles sobre el degradado.
     */
    fun getSecondaryTextColorForVehicleCard(
        vehicleColorId: String,
        scheme: QuipColorScheme = QuipColors.currentScheme
    ): Color {
        return scheme.colorTextSecondary
    }
}
